import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import FaqAccordion from "../../components/shared/FaqAccordion.jsx";
import { buildBreadcrumbJsonLd, buildFaqJsonLd } from "../../utils/jsonld.js";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import SponsorShowcase from "../../components/shared/SponsorShowcase.jsx";
import { SOCIAL_LINKS } from "../../components/shared/socialLinks.jsx";
import { ABOUT_PATH, SUBPAGE_PATH } from "./paths.js";
import { HISTORY } from "./history.js";
import { TEAM } from "./team.js";
import TeamTile from "./TeamTile.jsx";
import { INFO_FAQ } from "../infoFaq.js";
import { PRESS_PATH } from "../Press.jsx";

// `members` counts the real, currently-named team (see team.js) rather than
// a hand-typed number that can silently drift out of sync with it.
const STATS = [
  { key: "events", value: "1" },
  { key: "players", value: "20" },
  { key: "donationsRaised", value: "3K €" },
  { key: "members", value: String(TEAM.de.length) },
];

const RULEBOOK_PATH = {
  de: "/assets/brand/documents/Tales_and_Legends_Regelwerk_DE.pdf",
  en: "/assets/brand/documents/Tales_and_Legends_Regelwerk_EN.pdf",
};

const socialIconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "38px",
  height: "38px",
  borderRadius: "999px",
  background: "rgba(254,250,239,0.08)",
  color: "var(--color-cream)",
};

export default function AboutHub({ lang }) {
  const { t } = useTranslation();
  const faq = useMemo(() => INFO_FAQ[lang].flatMap((group) => group.items), [lang]);

  return (
    <>
      <SEOHead
        lang={lang}
        path={`/${ABOUT_PATH[lang]}`}
        altPath={`/${ABOUT_PATH[lang === "de" ? "en" : "de"]}`}
        title={t("about.metaTitle")}
        description={t("about.metaDescription")}
      />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.ueberUns"), path: `/${ABOUT_PATH[lang]}` },
          ],
          lang
        )}
      />
      <JsonLd data={buildFaqJsonLd(faq)} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "56px", paddingTop: "64px", paddingBottom: "96px" }}>
          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "32px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <p className="tl-eyebrow" style={{ margin: 0 }}>
                {t("about.eyebrow")}
              </p>
              <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", maxWidth: "24ch", margin: 0 }}>
                {t("about.title")}
              </h1>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "62ch", margin: 0 }}>{t("about.body")}</p>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "13px", color: "var(--color-cream-faint)" }}>{t("about.socialLabel")}</span>
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.key}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    style={socialIconButtonStyle}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ position: "relative", width: "100%", aspectRatio: "4 / 3", borderRadius: "var(--radius-card-lg)", overflow: "hidden" }}>
                <ResponsiveImage
                  src="/assets/brand/Presentation.png"
                  alt={t("about.imageAlt")}
                  priority
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
              <span style={{ fontSize: "12px", color: "var(--color-cream-faint)" }}>{t("about.imageCaption")}</span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: "1px", background: "rgba(254,250,239,0.1)", borderRadius: "var(--radius-card)", overflow: "hidden" }}>
            {STATS.map(({ key, value }) => (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "28px", background: "var(--color-bg)" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "34px", color: "var(--color-cream)" }}>{value}</span>
                <span style={{ fontSize: "13px", color: "var(--color-cream-faint)" }}>{t(`about.stats.${key}`)}</span>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
              {t("about.teamSectionTitle")}
            </h2>
            <p style={{ fontSize: "14px", color: "var(--color-cream-faint)", margin: 0 }}>{t("about.team.placeholder")}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: "20px" }}>
              {TEAM[lang].map((member, i) => (
                <TeamTile key={i} member={member} rolePlaceholder={t("about.team.rolePlaceholder")} />
              ))}
            </div>
            <Link to={`/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.team[lang]}`} style={{ fontSize: "13px", color: "var(--color-gold)", alignSelf: "flex-start" }}>
              {t("about.viewFullTeam")}
            </Link>
          </div>

          <div
            className="tl-grid-2"
            style={{ display: "grid", gridTemplateColumns: "1fr 1.05fr", gap: 0, borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(206,149,50,0.5)" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "18px", justifyContent: "center", padding: "48px 44px", background: "var(--color-bg)" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "16px", color: "var(--color-gold)" }}>
                {t("about.haltung.eyebrow")}
              </span>
              <h2 className="tl-page-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "34px", lineHeight: 1.12, color: "var(--color-cream)", maxWidth: "20ch", margin: 0 }}>
                {t("about.haltung.title")}
              </h2>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "48ch", margin: 0 }}>
                {t("about.haltung.body")}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", borderTop: "1px solid rgba(254,250,239,0.14)", paddingTop: "18px", marginTop: "4px" }}>
                <span style={{ fontSize: "16px", lineHeight: 1.65, color: "var(--color-cream-faint)", maxWidth: "46ch" }}>
                  {t("about.haltung.note")}
                </span>
                <Link to={`/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.conduct[lang]}`} style={{ fontSize: "14px", color: "var(--color-gold)" }}>
                  {t("about.haltung.cta")}
                </Link>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center", justifyContent: "center", padding: "40px", background: "var(--color-cream)" }}>
              <a
                href="https://www.mconis.de/larp-ist-bunt"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "block", width: "100%", maxWidth: "520px" }}
              >
                <ResponsiveImage
                  src="/assets/brand/larp_ist_bunt_freigestellt.png"
                  alt={t("about.haltung.imageAlt")}
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </a>
              <p style={{ fontSize: "12px", lineHeight: 1.5, color: "rgba(10,9,7,0.6)", maxWidth: "520px", margin: 0 }}>
                {t("about.haltung.credit")}
              </p>
            </div>
          </div>

          <SponsorShowcase lang={lang} />

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
              {t("about.timelineTitle")}
            </h2>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {HISTORY[lang].map((entry) => (
                <div
                  key={entry.date}
                  style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: "24px", padding: "20px 0", borderTop: "1px solid rgba(254,250,239,0.1)" }}
                >
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--color-gold)" }}>{entry.date}</span>
                  <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "64ch", margin: 0 }}>{entry.text}</p>
                </div>
              ))}
              <div style={{ borderTop: "1px solid rgba(254,250,239,0.1)" }} />
            </div>
            <Link to={`/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.history[lang]}`} style={{ fontSize: "13px", color: "var(--color-gold)", alignSelf: "flex-start" }}>
              {t("about.viewFullHistory")}
            </Link>
          </div>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <Link
              to={`/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.charter[lang]}`}
              className="tl-pill tl-pill-outline"
              style={{ padding: "13px 26px" }}
            >
              {t("about.charter.title")}
            </Link>
            <Link
              to={`/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.reports[lang]}`}
              className="tl-pill tl-pill-outline"
              style={{ padding: "13px 26px" }}
            >
              {t("about.reports.title")}
            </Link>
            <Link
              to={`/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.jugendschutz[lang]}`}
              className="tl-pill tl-pill-outline"
              style={{ padding: "13px 26px" }}
            >
              {t("about.jugendschutz.title")}
            </Link>
            <a
              href={RULEBOOK_PATH[lang]}
              target="_blank"
              rel="noopener noreferrer"
              className="tl-pill tl-pill-outline"
              style={{ padding: "13px 26px" }}
            >
              {t("about.rulebook.title")}
            </a>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px", borderTop: "1px solid rgba(254,250,239,0.1)", paddingTop: "40px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
              {t("info.faqTitle")}
            </h2>
            <FaqAccordion categories={INFO_FAQ[lang]} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-cream)", margin: 0 }}>
              {t("info.transparencyTitle")}
            </h2>
            <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>
              {lang === "de"
                ? "Tales&Legends gUG (haftungsbeschränkt), Amtsgericht Dresden, HRB 48120. Als gemeinnützig anerkannt vom Finanzamt Pirna (§ 60a AO, Bescheid vom 09.06.2026)."
                : "Tales&Legends gUG (limited liability), Amtsgericht Dresden, HRB 48120. Recognised as non-profit by the Finanzamt Pirna (§ 60a AO, notice dated 09.06.2026)."}
              {" "}
              <Link to={`/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.reports[lang]}`} style={{ color: "var(--color-gold)" }}>
                {t("about.reports.title")}
              </Link>
              {" · "}
              <Link to={`/${lang}/${PRESS_PATH[lang]}`} style={{ color: "var(--color-gold)" }}>
                {t("press.title")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
